/* ChatAI.jsx — Smart Log: AI-powered expense & production logging */

import React from 'react'
import { getClient } from '../db.jsx'
import { I } from '../icons.jsx'

// ── Animation CSS (injected once) ─────────────────────────────────────────────
const CHAT_STYLES = `
@keyframes wyfBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-5px); }
}
.wyf-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--text-faint);
  animation: wyfBounce 1.2s infinite ease-in-out;
  display: inline-block;
}
`;

// ── Session (persists 1 day) ──────────────────────────────────────────────────
function getOrCreateSession() {
  const stored = localStorage.getItem('wyf-chat-session');
  if (stored) {
    try {
      const { id, expires } = JSON.parse(stored);
      if (expires > Date.now()) return id;
    } catch { /* fall through */ }
  }
  const id = crypto.randomUUID();
  localStorage.setItem('wyf-chat-session', JSON.stringify({ id, expires: Date.now() + 86400000 }));
  return id;
}

// ── Inline markdown ───────────────────────────────────────────────────────────
function Md({ text }) {
  if (!text) return null;
  return (
    <>
      {text.split('\n').map((line, li) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <React.Fragment key={li}>
            {li > 0 && <br />}
            {parts.map((p, pi) =>
              p.startsWith('**') && p.endsWith('**')
                ? <strong key={pi}>{p.slice(2, -2)}</strong>
                : p
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

// ── Edge function caller ──────────────────────────────────────────────────────
async function callFunction(name, payload) {
  const supabase = getClient();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');
  const res = await fetch(`${window.WYF_CONFIG.SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': window.WYF_CONFIG.SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

// ── Upload image ──────────────────────────────────────────────────────────────
async function uploadImage(file) {
  const supabase = getClient();
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `receipts/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from('receipt-images').upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw new Error(`Upload failed: ${error.message}`);
  const { data: { publicUrl } } = supabase.storage.from('receipt-images').getPublicUrl(path);
  return publicUrl;
}

// ── AI avatar ─────────────────────────────────────────────────────────────────
function AiAvatar() {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: '#ffffff',
      border: '1.5px solid var(--border-strong)',
      display: 'grid', placeItems: 'center',
      flexShrink: 0, marginTop: 2,
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    }}>
      <img src="uploads/baguio-01.png" alt="WYF" style={{ width: 22, height: 22, objectFit: 'contain' }} />
    </div>
  );
}

// ── Bubble styles ─────────────────────────────────────────────────────────────
const AI_BUBBLE = {
  background: 'var(--surface-elev)',
  border: '1px solid var(--border)',
  padding: '10px 14px',
  borderRadius: '4px 16px 16px 16px',
  fontSize: 14, lineHeight: 1.5,
  color: 'var(--text)',
};

const USER_BUBBLE = {
  background: 'var(--grad)',
  color: '#fff',
  padding: '10px 14px',
  borderRadius: '16px 16px 4px 16px',
  fontSize: 14, lineHeight: 1.5,
};

// ── Typing indicator ──────────────────────────────────────────────────────────
function TypingBubble() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, alignSelf: 'flex-start' }}>
      <AiAvatar />
      <div style={AI_BUBBLE}>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', padding: '3px 0' }}>
          <span className="wyf-dot" style={{ animationDelay: '0ms' }} />
          <span className="wyf-dot" style={{ animationDelay: '190ms' }} />
          <span className="wyf-dot" style={{ animationDelay: '380ms' }} />
        </div>
      </div>
    </div>
  );
}

// ── Inline error ──────────────────────────────────────────────────────────────
function InlineError({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: 'rgba(248,113,113,0.10)',
      border: '1px solid rgba(248,113,113,0.28)',
      borderRadius: 10, padding: '10px 12px',
      fontSize: 13, color: 'var(--danger)',
    }}>
      <span style={{ flexShrink: 0, marginTop: 1, width: 16, height: 16 }}>{I.warn}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--danger)', padding: 0, lineHeight: 1 }}>
        <span style={{ width: 16, height: 16, display: 'block' }}>{I.x}</span>
      </button>
    </div>
  );
}

// ── Mode selector ─────────────────────────────────────────────────────────────
const MODES = [
  { id: 'receipt',    icon: I.expenses,   color: 'var(--cyan)', label: 'Log Expense',  desc: 'Snap a receipt photo' },
  { id: 'purchase',   icon: I.inventory,  color: 'var(--cream)', label: 'Log Purchase', desc: 'No receipt? Type it in' },
  { id: 'production', icon: I.flask,      color: 'var(--pink)', label: 'Log Batch',    desc: 'Describe what you made' },
];

function ModeSelector({ onSelect }) {
  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ fontSize: 12, color: 'var(--text-faint)', textAlign: 'center', marginBottom: 10 }}>
        What would you like to log?
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {MODES.map(m => (
          <button
            key={m.id}
            className="btn btn-ghost"
            style={{ flex: 1, padding: '14px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, borderRadius: 14, height: 'auto' }}
            onClick={() => onSelect(m.id)}
          >
            <span style={{ width: 24, height: 24, color: m.color }}>{m.icon}</span>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{m.label}</span>
            <span style={{ fontSize: 11, color: 'var(--text-faint)', textAlign: 'center', lineHeight: 1.3 }}>{m.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────
export function ChatAIView() {
  const sessionId = React.useMemo(() => getOrCreateSession(), []);
  const [messages, setMessages] = React.useState([]);
  const [mode, setMode] = React.useState(null);
  const [text, setText] = React.useState('');
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const bottomRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const clarifyMode = React.useRef(null);

  const lastMsg = messages[messages.length - 1];
  const awaitingClarification = lastMsg?.role === 'assistant' && lastMsg?.status === 'awaiting_clarification';

  // Inject animation CSS once
  React.useEffect(() => {
    if (document.getElementById('wyf-chat-styles')) return;
    const s = document.createElement('style');
    s.id = 'wyf-chat-styles';
    s.textContent = CHAT_STYLES;
    document.head.appendChild(s);
  }, []);

  // Messages + Realtime
  React.useEffect(() => {
    const supabase = getClient();
    if (!supabase) return;
    supabase.from('chat_messages').select('*').eq('session_id', sessionId).order('created_at', { ascending: true }).then(({ data }) => setMessages(data || []));
    const ch = supabase.channel(`chat-${sessionId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `session_id=eq.${sessionId}` }, (p) => setMessages((prev) => [...prev, p.new]))
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [sessionId]);

  React.useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { setError('Only JPEG, PNG, or WebP images are allowed.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB.'); return; }
    setError(null);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  async function insertUserMessage(content, imageUrl) {
    await getClient().from('chat_messages').insert({ session_id: sessionId, role: 'user', content, image_url: imageUrl || null, status: 'done' });
  }

  async function handleSend() {
    setError(null);
    const activeMode = awaitingClarification ? clarifyMode.current : mode;

    if (activeMode === 'receipt' && !awaitingClarification && !imageFile) {
      setError('Please attach a receipt photo first.'); return;
    }
    if (activeMode === 'purchase' && !text.trim()) {
      setError('Please describe what you bought.'); return;
    }
    if (activeMode === 'production' && !text.trim()) {
      setError('Please describe what you made.'); return;
    }

    setBusy(true);
    try {
      if (activeMode === 'receipt') {
        if (awaitingClarification) {
          await insertUserMessage(text.trim());
          await callFunction('process-receipt', { session_id: sessionId, clarification_text: text.trim() });
        } else {
          const imageUrl = await uploadImage(imageFile);
          await insertUserMessage(imageFile.name + (text.trim() ? ` — ${text.trim()}` : ''), imageUrl);
          clarifyMode.current = 'receipt';
          await callFunction('process-receipt', { session_id: sessionId, image_url: imageUrl });
        }
        setImageFile(null); setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

      } else if (activeMode === 'purchase') {
        await insertUserMessage(text.trim());
        clarifyMode.current = 'purchase';
        const payload = awaitingClarification
          ? { session_id: sessionId, clarification_text: text.trim() }
          : { session_id: sessionId, purchase_text: text.trim() };
        await callFunction('process-receipt', payload);

      } else if (activeMode === 'production') {
        await insertUserMessage(text.trim());
        clarifyMode.current = 'production';
        const payload = awaitingClarification
          ? { session_id: sessionId, clarification_text: text.trim() }
          : { session_id: sessionId, text: text.trim() };
        await callFunction('process-production', payload);
      }

      setText('');
      if (!awaitingClarification) setMode(null);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  function resetSession() {
    localStorage.removeItem('wyf-chat-session');
    window.location.reload();
  }

  const showModeSelector = !mode && !awaitingClarification && messages.length === 0;
  const showInput = mode !== null || awaitingClarification;

  const activeMeta = MODES.find(m => m.id === mode);
  const placeholder = awaitingClarification ? 'Type your reply…'
    : mode === 'receipt' ? 'Optional: Add a note (e.g. GCash, vendor name)'
    : mode === 'purchase' ? 'e.g. "12 eggs from Baguio Central Market ₱96, paid cash"'
    : 'e.g. "5 Original Flan today, used 3 cans Nestle Crema"';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', maxWidth: 720, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px 12px' }}>
        <div style={{ fontSize: 13, color: 'var(--text-faint)' }}>
          Session resets daily · {messages.length} message{messages.length !== 1 ? 's' : ''}
        </div>
        {messages.length > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={resetSession}>New session</button>
        )}
      </div>

      {/* Message list */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 8 }}>

        {messages.length === 0 && !showModeSelector && (
          <div style={{ textAlign: 'center', color: 'var(--text-faint)', fontSize: 13, marginTop: 40 }}>
            Select an action below to get started.
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id}>
            {msg.role === 'assistant' && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: '88%' }}>
                <AiAvatar />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {msg.image_url && (
                    <img src={msg.image_url} alt="Receipt" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 10, marginBottom: 6 }} />
                  )}
                  <div style={AI_BUBBLE}>
                    <Md text={msg.content} />
                    {msg.status === 'awaiting_clarification' && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-faint)', borderTop: '1px solid var(--border)', paddingTop: 5 }}>
                        Waiting for your reply ↓
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {msg.role === 'user' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ maxWidth: '80%' }}>
                  {msg.image_url && (
                    <img src={msg.image_url} alt="Receipt" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 10, marginBottom: 4 }} />
                  )}
                  <div style={USER_BUBBLE}>
                    <Md text={msg.content} />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {busy && <TypingBubble />}
        <div ref={bottomRef} />
      </div>

      {/* Mode selector */}
      {showModeSelector && <ModeSelector onSelect={(m) => { setMode(m); clarifyMode.current = m; }} />}
      {!showModeSelector && !showInput && messages.length > 0 && (
        <ModeSelector onSelect={(m) => { setMode(m); clarifyMode.current = m; }} />
      )}

      {/* Input area */}
      {showInput && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>

          {!awaitingClarification && activeMeta && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 14, height: 14, color: activeMeta.color }}>{activeMeta.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: activeMeta.color }}>{activeMeta.label}</span>
              <button
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 11, padding: '2px 8px', marginLeft: 'auto' }}
                onClick={() => { setMode(null); setImageFile(null); setImagePreview(null); setText(''); setError(null); }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Image upload — receipt mode only */}
          {mode === 'receipt' && !awaitingClarification && (
            <div>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} style={{ display: 'none' }} />
              {imagePreview ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={imagePreview} alt="Preview" style={{ height: 90, borderRadius: 8, objectFit: 'cover' }} />
                  <button
                    onClick={() => { setImageFile(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,.65)', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#fff' }}
                  >
                    <span style={{ width: 13, height: 13 }}>{I.x}</span>
                  </button>
                </div>
              ) : (
                <button className="btn btn-ghost" style={{ width: '100%', borderStyle: 'dashed', borderWidth: 2 }} onClick={() => fileInputRef.current?.click()}>
                  <span style={{ width: 17, height: 17 }}>{I.download}</span>
                  Attach receipt photo (max 5MB)
                </button>
              )}
            </div>
          )}

          {/* Text input + Send */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={2}
              style={{
                flex: 1, resize: 'none',
                background: 'var(--surface-elev)',
                border: '1px solid var(--border)',
                borderRadius: 10, padding: '10px 12px',
                color: 'var(--text)', fontSize: 14,
                fontFamily: 'inherit', lineHeight: 1.5,
              }}
            />
            <button
              className="btn btn-primary"
              style={{ padding: '10px 18px', borderRadius: 10, alignSelf: 'stretch' }}
              disabled={busy}
              onClick={handleSend}
            >
              {busy ? '…' : 'Send'}
            </button>
          </div>

          <InlineError message={error} onDismiss={() => setError(null)} />
        </div>
      )}
    </div>
  );
}
